import { MAX_PHONE_NUMBER_LENGTH, MAX_NICKNAME_LENGTH, MAX_PHONE_NUMBER_LENGTH_WITH_HYPHEN } from '@/consts';
import { INPUT_MSG } from '@/consts/message';
import { checkUniquePhoneNumber, tryCheckUniqueNickname } from '@/core/presenter/controller/JoinController';
import { useStores } from '@/stores';
import { removeHyphens } from '@/utils';
import { useState, useCallback } from 'react';

export default function useJoinUserInfo(onClick: () => void) {
  const { userStore, presentationStore } = useStores();
  const [phone, setPhone] = useState<string>('');
  const [phoneNumGuideMsg, setPhoneNumGuideMsg] = useState<string>('');
  const [nickname, setNickname] = useState<string>('');
  const [nicknameGuideMsg, setNicknameGuideMsg] = useState<string>('');
  const [nickNameCheckSuccess, setNickNameCheckSuccess] = useState<boolean>(false);
  const [phoneCheckSuccess, setPhoneCheckSuccess] = useState<boolean>(false);
  const isCompleteBtnDisabled = !nickNameCheckSuccess || !phoneCheckSuccess || !phoneCheckSuccess;

  /** 휴대폰 번호 입력 이벤트 */
  const onChangePhoneNumber = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    // 숫자만 추출
    const numbersOnly = value.replace(/\D/g, '');

    // 11자리 초과시 입력 무시
    if (numbersOnly.length > MAX_PHONE_NUMBER_LENGTH) return;

    // 숫자를 000-0000-0000 형식으로 변환
    let formattedNumber = '';
    if (numbersOnly.length <= 3) {
      formattedNumber = numbersOnly;
    } else if (numbersOnly.length <= 7) {
      formattedNumber = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3)}`;
    } else {
      formattedNumber = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3, 7)}-${numbersOnly.slice(7)}`;
    }

    setPhone(formattedNumber);
    // 휴대폰 번호가 변경되면 검증 성공 상태 초기화 (모바일 포함)
    setPhoneCheckSuccess(false);
  }, []);

  const onChangeNickname = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    // const sanitizedValue = value.replace(/[^a-zA-Z0-9가-힣]/g, '');
    const truncatedValue = value.slice(0, MAX_NICKNAME_LENGTH);
    setNickname(truncatedValue);
    // 닉네임이 변경되면 검증 성공 상태 초기화 (모바일 포함)
    setNickNameCheckSuccess(false);
  }, []);

  const onKeyDownNickname = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/^[a-zA-Z0-9가-힣]$/.test(e.key) &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
    ) {
      e.preventDefault();
    }
  }, []);

  /** 인증 번호 요청 버튼 클릭 이벤트 */
  const onClickRequestBtn = useCallback(async () => {
    if (phone.length < MAX_PHONE_NUMBER_LENGTH_WITH_HYPHEN) {
      setPhoneNumGuideMsg('휴대폰 양식이 올바르지 않습니다.');
      return;
    }

    const isUnique = await checkUniquePhoneNumber(removeHyphens(phone));

    if (isUnique.succed) {
      setPhoneNumGuideMsg(INPUT_MSG.CHECK_PHONE_SUCCESS);
      setPhoneCheckSuccess(true);
    } else {
      setPhoneNumGuideMsg('이미 등록된 휴대폰 번호입니다.');
    }
  }, [phone]);

  /** 닉네임 중복 체크 버튼 클릭 이벤트 */
  const onClickNicknameCheckBtn = useCallback(async () => {
    const isUnique = await tryCheckUniqueNickname(nickname);

    if (isUnique.succed) {
      setNicknameGuideMsg(INPUT_MSG.CHECK_NICKNAME_SUCCESS);
      setNickNameCheckSuccess(true);
    } else {
      setNicknameGuideMsg('이미 사용중인 닉네임입니다.');
      setNickNameCheckSuccess(false);
    }
  }, [nickname]);

  /** 완료 버튼 클릭 이벤트 */
  const onClickCompleteBtn = useCallback(() => {
    userStore.setPhone(removeHyphens(phone));
    userStore.setName(nickname);
    presentationStore.setPhoneNumber(removeHyphens(phone));

    onClick();
  }, [userStore, phone, nickname, presentationStore, onClick]);

  return {
    phone,
    phoneNumGuideMsg,
    isCompleteBtnDisabled,
    nickname,
    countryNumber: userStore.country ?? '',
    nicknameGuideMsg,
    onChangePhoneNumber,
    onClickRequestBtn,
    onChangeNickname,
    onKeyDownNickname,
    onClickNicknameCheckBtn,
    onClickCompleteBtn,
  };
}
